import { useState } from "react";

import { ImageProps as NextImageProps } from "next/image";
import NextImage from "next/image";

import classNames from "classnames/bind";
import { FocalPoint } from "resolvers/data/imageResolver";
import cmsImageLoader, { srcIsCmsAsset } from "utils/cmsImageLoader";

import styles from "./Image.module.scss";

const cx = classNames.bind(styles);

type ObjectFit = "fill" | "contain" | "cover" | "none" | "scale-down";

export type ImageProps = NextImageProps & {
    className?: string;
    alt: string;
    fade?: boolean;
    heroImage?: boolean;
    focalPoint?: FocalPoint;
    fill?: string;
    objectFit?: ObjectFit;
    layout?: string;
};

const getLoader = (src: ImageProps["src"]) => {
    return srcIsCmsAsset(src) ? cmsImageLoader : undefined;
};

const Image = (props: ImageProps) => {
    const [isVisible, setIsVisible] = useState(false);

    const {
        alt = "",
        className,
        layout = "responsive",
        loader = getLoader(props.src),
        fade = false,
        heroImage = false,
        sizes,
    } = props;

    const focalPoint = props?.focalPoint?.toLowerCase() ?? "center";

    if (!props.src) return null;

    return (
        <NextImage
            alt={alt}
            src={props.src}
            fill={layout === "fill"}
            width={layout !== "fill" ? props.width : undefined}
            height={layout !== "fill" ? props.height : undefined}
            loader={loader}
            quality={props.quality}
            priority={props.priority}
            loading={props.loading}
            placeholder={props.placeholder}
            blurDataURL={props.blurDataURL}
            unoptimized={props.unoptimized}
            style={{
                objectPosition: focalPoint,
                objectFit: props.objectFit,
            }}
            onLoad={(result) => {
                if (!fade) return;
                props.onLoad?.(result);
                setIsVisible(true);
            }}
            className={cx("image", { isVisible, heroImage, fade }, className)}
            sizes={sizes}
        />
    );
};

export default Image;
